/**
 * The Contact section of a person added in Keystone (#731), for staff and the manager: their name, email and phone,
 * with Edit, and Remove for someone added by mistake (asked first; refused once they have notes or history, which is
 * never deleted). CE21's people never show it: CE21 owns their details (the server refuses too). An email someone
 * else has is refused, naming them.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { conflictOf, editPerson, fieldErrorsOf, removePerson, type ConflictPerson, type PersonField } from "../../api/people";
import { pushedForSheet } from "../app/oneSheet";
import type { PersonIdentity } from "../../api/person360.types";
import { invalidate } from "../data/useQuery";
import { Button, Dialog, SheetRows, SheetSection, useToast } from "../kit";
import { checkDraft, trimmed, type Draft, type FieldErrors } from "../screens/people/personDraft";
import { PersonFields } from "../screens/people/personForm";

type Row = readonly [label: string, value: ReactNode];

const draftOf = (identity: PersonIdentity): Draft => ({
  first_name: identity.first_name ?? "",
  last_name: identity.last_name ?? "",
  email: identity.email ?? "",
  phone: identity.phone.mobile ?? "",
});

export function EditableContact({ identity, rows }: { identity: PersonIdentity; rows: readonly Row[] }) {
  const toast = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [owners, setOwners] = useState<ConflictPerson[]>([]);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  /** Their sheet closes as the host's Close does: back over the entry that opened it, else the parameter out. */
  const closeSheet = () => {
    if (pushedForSheet(location.state)) return void navigate(-1);
    const next = new URLSearchParams(params);
    next.delete("person");
    setParams(next, { replace: true });
  };

  const remove = async () => {
    setBusy(true);
    try {
      await removePerson(identity.id);
      setAsking(false);
      void invalidate("people:");
      toast.show({ message: "Person removed" });
      closeSheet();
    } catch (error) {
      setAsking(false);
      toast.show({ message: conflictOf(error)?.detail || "Could not remove them. Try again." });
    } finally {
      setBusy(false);
    }
  };

  const stop = () => {
    setDraft(null);
    setErrors({});
    setOwners([]);
  };

  if (draft === null) {
    return (
      <SheetSection title="Contact">
        {rows.length > 0 && <SheetRows rows={rows} />}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setAsking(true)}>
            Remove
          </Button>
          <Button variant="surface" size="sm" onClick={() => setDraft(draftOf(identity))}>
            Edit
          </Button>
        </div>
        <Dialog
          open={asking}
          onClose={() => setAsking(false)}
          title={`Remove ${identity.display_name}?`}
          description="For someone added by mistake. Anyone with notes or history stays."
          footer={
            <>
              <Button variant="surface" data-autofocus onClick={() => setAsking(false)} disabled={busy}>
                Keep
              </Button>
              <Button variant="primary" onClick={() => void remove()} disabled={busy}>
                Remove
              </Button>
            </>
          }
        />
      </SheetSection>
    );
  }

  const change = (key: PersonField, value: string) => {
    setDraft({ ...draft, [key]: value });
    setErrors((now) => ({ ...now, [key]: undefined }));
    if (key === "email") setOwners([]);
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const found = checkDraft(draft);
    if (Object.keys(found).length > 0) return setErrors(found);
    const now = trimmed(draft);
    const was = draftOf(identity);
    const changes = Object.fromEntries((Object.keys(now) as PersonField[]).filter((k) => now[k] !== was[k]).map((k) => [k, now[k]]));
    if (Object.keys(changes).length === 0) return stop();
    setBusy(true);
    try {
      await editPerson(identity.id, changes);
      await invalidate(`v2:person:${identity.id}`);
      void invalidate("people:");
      toast.show({ message: "Saved" });
      stop();
    } catch (error) {
      const conflict = conflictOf(error);
      if (conflict?.conflict === "email") {
        setOwners(conflict.people);
        setErrors({ email: "Someone else in Keystone has this email." });
      } else {
        const fields = fieldErrorsOf(error);
        if (fields && Object.keys(fields).length > 0) setErrors(fields);
        else toast.show({ message: conflict?.detail || "Could not save. Try again." });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <SheetSection title="Contact">
      <form noValidate onSubmit={(event) => void save(event)} className="flex flex-col gap-4">
        <PersonFields draft={draft} errors={errors} onChange={change} emailOwners={owners} disabled={busy} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={stop} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="surface" size="sm" disabled={busy || owners.length > 0}>
            Save
          </Button>
        </div>
      </form>
    </SheetSection>
  );
}
